import React, { useCallback, useEffect, useState } from 'react';
import axiosClient from '../../../api/axiosClient';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { CircularProgress } from '@mui/material';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import { buildShopScopedPath } from '../../../utils/adminShopContext';
import { useTranslation } from 'react-i18next';

const ACCENT = '#7d6633';

const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900/60 dark:backdrop-blur-xl ' +
      className
    }
  >
    {children}
  </div>
);

const CardHeader = ({ title, icon, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
  </div>
);

const emptyProfile = {
  name: '',
  address: '',
  phone: '',
  email: '',
};

const inputClassName = 'w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all';
const readonlyClassName = 'w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-zinc-100/70 dark:bg-zinc-800/70 backdrop-blur-sm text-zinc-600 dark:text-zinc-300 cursor-not-allowed';

const trimProfile = (profile) => ({
  name: profile.name.trim(),
  address: profile.address.trim(),
  phone: profile.phone.trim(),
  email: profile.email.trim(),
});

const isValidEmail = (email) => !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const BusinessProfileSettings = () => {
  const { shopId } = useAdminAuth();
  const { t } = useTranslation('admin');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(emptyProfile);
  const [timezone, setTimezone] = useState('');

  const loadProfile = useCallback(async () => {
    if (!shopId) return;

    try {
      setLoading(true);
      const [profileResponse, settingsResponse] = await Promise.all([
        axiosClient.get(buildShopScopedPath('/admin/settings/profile', shopId)),
        axiosClient.get(buildShopScopedPath('/admin/settings/shop', shopId)).catch(() => ({ data: {} })),
      ]);

      setProfile({
        name: profileResponse.data?.name || '',
        address: profileResponse.data?.address || '',
        phone: profileResponse.data?.phone || '',
        email: profileResponse.data?.email || '',
      });
      setTimezone(settingsResponse.data?.timezone || '');
    } catch (error) {
      console.error('Error loading business profile:', error);
      toast.error(t('admin.business_profile_error'));
    } finally {
      setLoading(false);
    }
  }, [shopId, t]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleInputChange = (field, value) => {
    setProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateProfile = (payload) => {
    if (!payload.name) {
      toast.error(t('admin.business_profile_name_required'));
      return false;
    }

    if (!isValidEmail(payload.email)) {
      toast.error(t('admin.business_profile_email_invalid'));
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    const payload = trimProfile(profile);
    if (!validateProfile(payload)) return;

    try {
      setSaving(true);
      const response = await axiosClient.put(buildShopScopedPath('/admin/settings/profile', shopId), payload);
      const savedProfile = response.data?.shop || response.data || {};

      setProfile({
        name: savedProfile.name || payload.name,
        address: savedProfile.address || payload.address,
        phone: savedProfile.phone || payload.phone,
        email: savedProfile.email || payload.email,
      });

      const settingsResponse = await axiosClient
        .get(buildShopScopedPath('/admin/settings/shop', shopId))
        .catch(() => ({ data: {} }));
      setTimezone(settingsResponse.data?.timezone || timezone);

      toast.success(t('admin.business_profile_saved'));
    } catch (error) {
      console.error('Error saving business profile:', error);
      const message = error.response?.data?.errors?.[0]
        || error.response?.data?.message
        || t('admin.business_profile_error');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex items-center gap-3 text-zinc-600 dark:text-zinc-400">
          <CircularProgress size={32} style={{ color: ACCENT }} />
          <span>{t('admin.settings_loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader
        title={t('admin.business_profile_title')}
        subtitle={t('admin.business_profile_subtitle')}
        icon={
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M5 21V7l8-4 6 3v15M9 9h1m-1 4h1m4-4h1m-1 4h1M9 21v-4h6v4" />
          </svg>
        }
      />

      <div className="p-4 md:p-5">
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.business_profile_name')} *
              </label>
              <input
                type="text"
                value={profile.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={inputClassName}
                style={{ '--accent': ACCENT }}
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.business_profile_timezone')}
              </label>
              <input
                type="text"
                value={timezone || ''}
                readOnly
                placeholder={t('admin.business_profile_timezone_unavailable')}
                className={readonlyClassName}
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.business_profile_address')}
              </label>
              <textarea
                value={profile.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                rows={3}
                className={inputClassName}
                style={{ '--accent': ACCENT }}
              />
              <p className="text-xs mt-2 text-amber-700 dark:text-amber-300">
                {t('admin.business_profile_timezone_note')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.business_profile_phone')}
              </label>
              <input
                type="tel"
                value={profile.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={inputClassName}
                style={{ '--accent': ACCENT }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                {t('admin.business_profile_email')}
              </label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={inputClassName}
                style={{ '--accent': ACCENT }}
              />
            </div>

          </div>
        </div>

        <div className="px-4 md:px-5 pb-4 md:pb-5 pt-4 border-t border-white/10 dark:border-white/10 mt-6">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSave}
            disabled={saving}
            className="w-full sm:w-auto px-6 py-2.5 text-sm rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: ACCENT }}
          >
            {saving ? (
              <>
                <CircularProgress size={16} style={{ color: 'white' }} />
                <span>{t('admin.settings_saving_changes')}</span>
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{t('admin.business_profile_save')}</span>
              </>
            )}
          </motion.button>
        </div>
      </div>
    </Card>
  );
};

export default BusinessProfileSettings;
